<script setup lang="ts">
import { RouterLink, RouterView } from 'vue-router'
import { watchEffect } from 'vue'
import NotificationContainer from './components/NotificationContainer.vue'
import NetworkStatus from './components/NetworkStatus.vue'
import UnauthorizedScreen from './components/UnauthorizedScreen.vue'
import AuthErrorScreen from './components/AuthErrorScreen.vue'
import { useAuth } from './composables/useAuth'

const { authState, checkScope, login } = useAuth()

// Watch for authentication changes and check scope
watchEffect(() => {
  if (authState.value.isLoading) return

  if (authState.value.isAuthenticated) {
    checkScope()
  } else if (!authState.value.error) {
    // Automatically attempt login
    login()
  }
})
</script>

<template>
  <!-- Loading screen -->
  <div v-if="authState.isLoading" class="min-h-screen bg-gray-50 flex items-center justify-center">
    <div class="text-center">
      <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-4 mx-auto">
        <svg class="w-8 h-8 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
        </svg>
      </div>
      <p class="text-gray-600">Loading...</p>
    </div>
  </div>

  <!-- Blank screen for authenticated users without required scope -->
  <UnauthorizedScreen v-else-if="authState.isAuthenticated && !authState.hasRequiredScope" />

  <!-- Error screen for authentication failures -->
  <AuthErrorScreen v-else-if="authState.error" />
  
  <!-- Main app for authenticated users with correct scope -->
  <div v-else class="min-h-screen bg-gray-50">
    <header class="bg-white/80 backdrop-blur-md shadow-sm border-b border-gray-200 sticky top-0 z-40">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <div class="flex items-center">
            <div class="flex items-center space-x-3">
              <div class="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <h1 class="text-xl font-bold text-gray-900">Delivery Manager</h1>
            </div>
          </div>
          <div class="flex items-center space-x-8">
            <nav class="hidden md:flex items-center space-x-1">
              <RouterLink 
                to="/" 
                class="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200"
              >
                Home
              </RouterLink>
              <RouterLink 
                to="/inventory" 
                class="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200"
              >
                Inventory
              </RouterLink>
              <RouterLink 
                to="/add" 
                class="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200"
              >
                Add Order
              </RouterLink>
              <RouterLink 
                to="/orders" 
                class="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200"
              >
                Active Orders
              </RouterLink>
              <RouterLink 
                to="/settings" 
                class="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200"
              >
                Settings
              </RouterLink>
            </nav>
            <div class="flex items-center">
              <NetworkStatus />
            </div>
          </div>
        </div>
      </div>
    </header>

    <main class="flex-1 pb-20 md:pb-0">
      <RouterView />
    </main>

    <!-- Mobile Bottom Navigation -->
    <nav class="bottom-nav md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-gray-200 z-50">
      <div class="grid grid-cols-4 h-16">
        <RouterLink 
          to="/inventory" 
          class="flex flex-col items-center justify-center text-xs font-medium text-gray-600 hover:text-blue-600 transition-colors"
        >
          <svg class="w-5 h-5 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
          </svg>
          Inventory
        </RouterLink>
        <RouterLink 
          to="/orders" 
          class="flex flex-col items-center justify-center text-xs font-medium text-gray-600 hover:text-blue-600 transition-colors"
        >
          <svg class="w-5 h-5 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"></path>
          </svg>
          Orders
        </RouterLink>
        <RouterLink 
          to="/add" 
          class="add-order-btn relative flex flex-col items-center justify-center text-xs font-medium transition-all duration-200"
        >
          <div class="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 mb-1 -mt-6">
            <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
          </div>
          <span class="text-blue-600 font-semibold">Add</span>
        </RouterLink>
        <RouterLink 
          to="/settings" 
          class="flex flex-col items-center justify-center text-xs font-medium text-gray-600 hover:text-blue-600 transition-colors"
        >
          <svg class="w-5 h-5 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
          </svg>
          Settings
        </RouterLink>
      </div>
    </nav>

    <!-- Global notification container -->
    <NotificationContainer />
  </div>
</template>

<style scoped>
/* Desktop navigation active styles */
header nav .router-link-active {
  @apply bg-blue-50 text-blue-700 font-semibold;
}

header nav .router-link-active:hover {
  @apply bg-blue-100;
}

/* Mobile bottom navigation active styles */
.bottom-nav .router-link-active {
  @apply text-blue-600;
}

.bottom-nav .router-link-active svg {
  @apply text-blue-600;
}

/* Special styling for active add order button */
.bottom-nav .add-order-btn.router-link-active div {
  @apply from-blue-600 to-blue-700 shadow-xl scale-105;
}

.bottom-nav .add-order-btn.router-link-active span {
  @apply text-blue-700;
}
</style>
